// Generated by Claude Code
/**
 * Loads the company-side data the role editor needs to flag dependency-tier
 * permission warnings — form-settings (which fields a company marks required /
 * visible) joined with the limited-access whitelists (which properties each
 * limited slot exposes). The backend keeps these systems independent, so this
 * builds the normalized {@link PermissionDependencyContext} the pure warnings
 * engine consumes.
 *
 * Every fetch is best-effort: a caller who can edit roles may still lack read
 * access to an entity's form-settings or the limited-access config, so failures
 * degrade to "skip that check" rather than blocking the editor.
 */
import i18n from 'i18next'
import FormSettingsApi from '#core/api/operations/FormSettingsApi.ts'
import LimitedAccessApi from '#core/api/operations/LimitedAccessApi.ts'
import { PermissionService } from '#core/entities/auth/roles/Permission.ts'
import type {
  DependencyFormField,
  PermissionDependencyContext,
} from '#core/entities/auth/roles/permission-warnings.ts'
import type FormSettings from '#core/entities/operations/settings/FormSettings.ts'
import type { FormSettingsEntityType } from '#core/entities/operations/settings/FormSettings.ts'

const formSettingsApi = new FormSettingsApi()
const limitedAccessApi = new LimitedAccessApi()

interface EntityConfig {
  entityType: FormSettingsEntityType
  service: PermissionService
  /** i18n key prefix (in the `settings` namespace) for this entity's field labels. */
  labelPrefix: string
  /** Fields always required regardless of company form-settings (identity fields). */
  alwaysRequired: string[]
  /** Relational fields → the module a user must be able to view to use them. */
  relational: Partial<Record<string, PermissionService>>
}

// Entities with form-settings + limited-access support. Relational maps are curated
// cross-module links a required/visible field depends on.
const ENTITY_CONFIGS: EntityConfig[] = [
  {
    entityType: 'asset',
    service: PermissionService.ASSETS,
    labelPrefix: 'assetsSettings.fields',
    alwaysRequired: ['name'],
    relational: { vendor_id: PermissionService.VENDORS, parent_id: PermissionService.LOCATIONS },
  },
  {
    entityType: 'work_order',
    service: PermissionService.WORK_ORDERS,
    labelPrefix: 'workOrdersSettings.fields',
    alwaysRequired: ['title'],
    relational: {},
  },
  {
    entityType: 'work_request',
    service: PermissionService.WORK_ORDER_REQUESTS,
    labelPrefix: 'workRequestsSettings.fields',
    alwaysRequired: ['title'],
    relational: { asset_id: PermissionService.ASSETS },
  },
]

/** Resolves a field key to its localized label, falling back to the raw key. */
function labelFor(config: EntityConfig, key: string): string {
  return i18n.t(`${config.labelPrefix}.${key}`, { ns: 'settings', defaultValue: key })
}

/** Normalizes one entity's form-settings into the fields the warnings engine checks. */
function buildFields(config: EntityConfig, formSettings: FormSettings | null): DependencyFormField[] {
  const overrides = formSettings?.settings.fields ?? {}
  const byKey = new Map<string, DependencyFormField>()

  const upsert = (key: string, patch: Partial<DependencyFormField>) => {
    const existing =
      byKey.get(key) ?? { key, label: labelFor(config, key), required: false, visible: true }
    byKey.set(key, { ...existing, ...patch })
  }

  // Fields the company explicitly marks required (required && hidden is rejected backend-side).
  for (const [key, override] of Object.entries(overrides)) {
    if (override?.required) upsert(key, { required: true, visible: !override.hidden })
  }
  // Identity fields that are always required regardless of form-settings.
  for (const key of config.alwaysRequired) upsert(key, { required: true, visible: true })
  // Relational fields, visible unless the company hid them.
  for (const [key, linkedService] of Object.entries(config.relational)) {
    if (!linkedService) continue
    const override = overrides[key]
    if (override?.hidden) continue
    upsert(key, { visible: true, required: override?.required ?? false, linkedService })
  }

  return [...byKey.values()]
}

/**
 * Fetches and normalizes the company form-settings + limited-access whitelists
 * into a {@link PermissionDependencyContext}. Always resolves (never rejects);
 * anything that fails to load is simply omitted, disabling only its checks.
 */
export async function loadPermissionDependencyContext(): Promise<PermissionDependencyContext> {
  const formFieldsByService: NonNullable<PermissionDependencyContext['formFieldsByService']> = {}
  const limitedWhitelistsByService: NonNullable<PermissionDependencyContext['limitedWhitelistsByService']> = {}

  const [formResults, slots] = await Promise.all([
    Promise.all(
      ENTITY_CONFIGS.map(async (config) => {
        try {
          return { config, formSettings: await formSettingsApi.getFormSettings(config.entityType) }
        } catch {
          return { config, formSettings: null }
        }
      }),
    ),
    limitedAccessApi.listConfig().catch(() => null),
  ])

  for (const { config, formSettings } of formResults) {
    const fields = buildFields(config, formSettings)
    if (fields.length > 0) formFieldsByService[config.service] = fields
  }

  if (slots) {
    for (const slot of slots) {
      const map = limitedWhitelistsByService[slot.service] ?? {}
      map[slot.action] = slot.properties
      limitedWhitelistsByService[slot.service] = map
    }
  }

  return { formFieldsByService, limitedWhitelistsByService }
}
